import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { BookOpen, CheckCircle2, Circle, Lock, ArrowLeft, Play, FileText, Download } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { CountdownTimer } from '@/components/course/countdown-timer';

interface Course {
  id: string;
  title: string;
  description: string;
  profiles: {
    full_name: string;
  };
}

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  duration: number;
  order_index: number;
  is_preview: boolean;
}

interface Progress {
  lesson_id: string;
  completed: boolean;
}

interface LiveSession {
  id: string;
  title: string;
  scheduled_at: string;
  status: string;
}

export default function LearnCoursePage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [nextLiveSession, setNextLiveSession] = useState<LiveSession | null>(null);

  const courseId = params.id as string;

  useEffect(() => {
    if (profile) {
      checkEnrollmentAndFetchData();
    }
  }, [profile, courseId]);

  const checkEnrollmentAndFetchData = async () => {
    setLoading(true);

    const { data: enrollment } = await supabase
      .from('enrollments')
      .select('id')
      .eq('course_id', courseId)
      .eq('student_id', profile?.id)
      .maybeSingle();

    if (!enrollment) {
      setIsEnrolled(false);
      setLoading(false);
      return;
    }

    setIsEnrolled(true);
    setEnrollmentId(enrollment.id);

    await Promise.all([
      fetchCourse(),
      fetchLessons(),
      fetchProgress(enrollment.id),
      fetchNextLiveSession(),
    ]);

    setLoading(false);
  };

  const fetchCourse = async () => {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        id,
        title,
        description,
        teacher_id(full_name)
      `)
      .eq('id', courseId)
      .maybeSingle();

    if (data) {
      setCourse({
        id: data.id,
        title: data.title,
        description: data.description,
        profiles: data.teacher_id as any,
      });
    }
  };

  const fetchLessons = async () => {
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('course_id', courseId)
      .order('order_index');

    if (data) {
      setLessons(data);
      if (data.length > 0 && !selectedLesson) {
        setSelectedLesson(data[0]);
      }
    }
  };

  const fetchProgress = async (enrollmentId: string) => {
    const { data } = await supabase
      .from('course_progress')
      .select('lesson_id, completed')
      .eq('enrollment_id', enrollmentId);

    if (data) {
      setProgress(data);
    }
  };

  const fetchNextLiveSession = async () => {
    const { data: liveSessions } = await supabase
      .from('live_sessions')
      .select('id, title, scheduled_at, status, duration_minutes')
      .eq('course_id', courseId)
      .in('status', ['scheduled', 'live'])
      .order('scheduled_at', { ascending: true });

    if (liveSessions && liveSessions.length > 0) {
      const now = new Date();

      const activeOrUpcoming = liveSessions.find(session => {
        if (session.status === 'live') {
          return true;
        }

        const scheduledAt = new Date(session.scheduled_at);
        const sessionEnd = new Date(scheduledAt.getTime() + (session.duration_minutes || 60) * 60000);

        return scheduledAt > now || (scheduledAt <= now && sessionEnd > now);
      });

      if (activeOrUpcoming) {
        setNextLiveSession(activeOrUpcoming);
      }
    }
  };

  const isLessonCompleted = (lessonId: string) => {
    return progress.some(p => p.lesson_id === lessonId && p.completed);
  };

  const handleMarkAsComplete = async (lessonId: string) => {
    if (!enrollmentId) return;

    const existingProgress = progress.find(p => p.lesson_id === lessonId);

    if (existingProgress) {
      const { error } = await supabase
        .from('course_progress')
        .update({ completed: true, completed_at: new Date().toISOString() })
        .eq('enrollment_id', enrollmentId)
        .eq('lesson_id', lessonId);

      if (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile aggiornare il progresso',
          variant: 'destructive',
        });
        return;
      }
    } else {
      const { error } = await supabase
        .from('course_progress')
        .insert({
          enrollment_id: enrollmentId,
          lesson_id: lessonId,
          completed: true,
          completed_at: new Date().toISOString(),
        });

      if (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile salvare il progresso',
          variant: 'destructive',
        });
        return;
      }
    }

    await fetchProgress(enrollmentId);
    await updateCourseProgress();

    toast({
      title: 'Lezione completata',
      description: 'Ottimo lavoro! Continua così.',
    });
  };

  const updateCourseProgress = async () => {
    if (!enrollmentId || lessons.length === 0) return;

    const completedLessons = progress.filter(p => p.completed).length;
    const totalLessons = lessons.length;
    const percentage = Math.round((completedLessons / totalLessons) * 100);

    await supabase
      .from('enrollments')
      .update({ progress_percentage: percentage })
      .eq('id', enrollmentId);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-48" />
          <div className="grid gap-6 lg:grid-cols-3">
            <Skeleton className="h-96 lg:col-span-2" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!isEnrolled) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Lock className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Non sei iscritto a questo corso
            </h3>
            <p className="text-gray-600 text-center mb-4">
              Devi iscriverti al corso per accedere ai contenuti
            </p>
            <Link to={`/dashboard/courses/${courseId}`}>
              <Button>Vai al corso</Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Corso non trovato
            </h3>
            <Link to="/dashboard/my-learning">
              <Button>Torna ai miei corsi</Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const completedCount = progress.filter(p => p.completed).length;
  const totalCount = lessons.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/dashboard/my-learning">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai miei corsi
            </Button>
          </Link>
          <Badge variant="secondary">
            {completedCount} / {totalCount} lezioni completate
          </Badge>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            {selectedLesson ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-2xl">{selectedLesson.title}</CardTitle>
                      {selectedLesson.description && (
                        <CardDescription className="mt-2 text-base">
                          {selectedLesson.description}
                        </CardDescription>
                      )}
                    </div>
                    {isLessonCompleted(selectedLesson.id) && (
                      <Badge className="bg-green-600 ml-4">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Completata
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {selectedLesson.video_url ? (
                    <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                      <video
                        src={selectedLesson.video_url}
                        controls
                        className="w-full h-full"
                      >
                        Il tuo browser non supporta i video.
                      </video>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">
                          Il video per questa lezione sarà disponibile a breve
                        </p>
                      </div>
                    </div>
                  )}

                  {!isLessonCompleted(selectedLesson.id) && (
                    <Button
                      onClick={() => handleMarkAsComplete(selectedLesson.id)}
                      className="w-full"
                      size="lg"
                    >
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Segna come completata
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Nessuna lezione disponibile
                  </h3>
                  <p className="text-gray-600 text-center">
                    Il docente aggiungerà presto i contenuti del corso
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            {nextLiveSession && (
              <CountdownTimer
                sessionId={nextLiveSession.id}
                title={nextLiveSession.title}
                scheduledAt={nextLiveSession.scheduled_at}
                status={nextLiveSession.status}
              />
            )}

            <Card>
              <CardHeader>
                <CardTitle>{course.title}</CardTitle>
                <CardDescription>
                  Docente: {course.profiles?.full_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progresso totale</span>
                    <span className="font-semibold">{progressPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Lezioni del corso</CardTitle>
              </CardHeader>
              <CardContent>
                {lessons.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-4">
                    Nessuna lezione disponibile
                  </p>
                ) : (
                  <div className="space-y-2">
                    {lessons.map((lesson, index) => (
                      <button
                        key={lesson.id}
                        onClick={() => setSelectedLesson(lesson)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedLesson?.id === lesson.id
                            ? 'bg-blue-50 border-2 border-blue-500'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            {isLessonCompleted(lesson.id) ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">
                              {index + 1}. {lesson.title}
                            </p>
                            {lesson.duration > 0 && (
                              <p className="text-xs text-gray-600 mt-1">
                                {lesson.duration} min
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
